# Terms & License

Last updated: June 9, 2026

universal-sign-in.com (the "Licensor", "Site", "we", "us", or "our") is a website operated by Xplat-soft s.r.o. registered under company number 22539514, address: Jaurisova 515/4, Michle, 140 00 Prague, Czech Republic. VAT number is CZ22539514.

By accessing or purchasing from us, you agree to be bound by these Terms, the license, as well as our Privacy Policy. If you do not agree, please do not use our services. These Terms and license apply to all versions of the Software (as defined below), and we may update them from time to time. If that happens, we will post the revised document on the Site, update the "Last updated" date, and take any other steps required by applicable law.

---

<br />

This is a subscription-based commercial license for the Universal Sign In version of react-native-google-signin.

## Which License Do I Need?

**Choose Personal License if you are:**

- A solo developer working on your own projects
- An individual freelancer or consultant working independently
- Working on personal or side projects

**Choose Team License if:**

- You're part of a company or organization
- Multiple people (employees, contractors) need to access the package
- You're building products for a business entity

## Personal License Summary

- **Grant**:
  - for solo developers and individual freelancers working independently (non-transferable)
- **What's Allowed**:
  - Create unlimited Products, including commercial ones
  - Continue using your last version even after the subscription ends (you need to store the package in a non-public location, access to the package registry stops working at that time)
- **What's Not Allowed**:
  - Redistributing the code separately from a Product
  - Sharing access with other entities (individuals, companies, etc.)
  - Reselling or creating derivative Products
- **Updates**: Available during active subscription (duration is one year, renews automatically)

## Team License Summary

- **Grant**:
  - for companies, teams, and organizations - up to 15 individuals (employees and contractors) per license
- **What's Allowed**:
  - Create unlimited Products, including commercial ones
  - Continue using your last version even after the subscription ends (you need to store the package in a non-public location, access to the package registry stops working at that time)
- **What's Not Allowed**:
  - Redistributing the code separately from a Product
  - Sharing access with non-team members
  - Reselling or creating derivative Products
- **Updates**: Available during active subscription (duration is one year, renews automatically)

## Former Mini License

- no longer available but follows the same rules as Team License, but has a different number of Seats

---

## 1. Grant of License

Xplat-soft s.r.o. grants you a non-exclusive, non-transferable, limited license to use the "Universal Sign In" version of `@react-native-google-signin/google-signin` package ("RNGS") in accordance with the terms of this license.

In the case of Personal License, the license grants permission for one individual to access and use RNGS. In the case of Team License, the license grants permission for several (as indicated by the subscription plan on our website) individuals (employees and contractors of the business entity) to access and use RNGS.

The Seats are not to be reassigned to different users during the subscription period without explicit permission from the Licensor.

## 2. Definitions

- **"Licensee"** refers to any individual or business entity who has purchased a license.
- **"RNGS" (also "Software")** refers to the source code, compiled binaries, CLI Tools, and other resources made available to the Licensee after purchasing a RNGS license.
- **"Product"** refers to any artifact produced that incorporates RNGS or derivatives of RNGS.
- **"Employee"** refers to any individual employed by the Licensee on a full-time or part-time basis.
- **"Contractor"** refers to any individual or business entity contracted to perform services for the Licensee.
- **"Seats"** refers to the number of individuals who are authorized to use RNGS under the respective license. The number of seats is given by the subscription plan.

## 3. Scope of License

This license permits the Licensee to use Software in an unlimited number of Products, including those of a commercial nature. For team license, this right extends to Employees and Contractors of the Licensee for work directly related to the Licensee's projects.

## 4. Restrictions

The Licensee shall not:

- Re-sell or re-distribute RNGS, in whole or in part, to any third party.
- Create a service that allows third parties to use RNGS to build a Product.
- Grant access to RNGS to individuals who are not authorized. For team licenses, only Employees and Contractors of the Licensee are authorized. For personal licenses, only the individual Licensee is authorized.
- Develop or distribute a code template, starter kit, library, or similar developer resource that includes RNGS.
- Circumvent, disable, or tamper with authentication mechanisms in CLI Tools.
- Share authentication credentials with non-authorized individuals.

## 5. Termination

- The license is valid as long as the Licensee maintains their subscription (pays a subscription fee) with the Licensor. Upon termination of the subscription, the Licensee loses the access to private npm registry where package is hosted. They also lose the right to receive updates, new versions, or additional materials related to RNGS. However, if the Licensee was in full compliance with this License during the subscription period, Licensee retains the right to continue using the version of RNGS they obtained before their subscription ended. Licensee, at their sole responsibility, needs to ensure they keep a copy of RNGS for use after the subscription ends.
- This continuing right is non-transferable and is limited to the Licensee's internal use. For team licenses, this includes use by the Employees and Contractors who were authorized during the subscription period.
- The Licensor reserves the right to revoke this continuing right from any Licensee in the event of misuse of RNGS as detailed in the Restrictions section of this license or breach of any other terms of this license.
- License duration is one year unless indicated differently at the payment portal.
- Promotional discounts, coupon codes, or similar offers apply only to the initial subscription period unless explicitly stated otherwise. Renewals are billed at the undiscounted subscription price shown at the time of purchase.
- The Licensor may, at its discretion, suspend or revoke access to the Software or related services if it reasonably suspects a violation of this License, pending investigation or resolution. Such past violations do not prevent the Licensee from purchasing a new license or continuing use under a new subscription, provided the license terms are met.
- If this License is terminated due to breach by the Licensee, the Licensee must immediately cease all use of RNGS, delete all copies in their possession or control, and, if requested by the Licensor, certify in writing that they have done so. Licensee may not rely on any continuing rights described above.

## 6. No Warranty

The Software is provided "as is", without warranty of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. Licensor hereby disclaims any warranty that the Software shall be error free, without defects or code which may cause damage to Licensee's computers, Licensee's software, or to the Licensee, and that Software shall be functional. Nothing in this Grant of License shall require the Licensor to provide the Licensee with support or fixes to any bug, failure, performance issue, or other defect in the Software.

## 7. Limitation of Liability

In no event shall the Licensor be liable for any claim, damages, or other liability arising from the use of RNGS, whether in an action of contract, tort, or otherwise. The Licensor shall not be liable for any failure or delay in performance caused by circumstances beyond its reasonable control, including but not limited to acts of God, natural disasters, labor disputes, or interruptions of internet infrastructure or the package registry through which the package is distributed. Licensee shall be solely liable to any damage, defect or loss incurred as a result of operating the Software and undertake the risks contained in running the Software.

## 8. Governing Law, Jurisdiction

This license shall be governed by the laws of the Czech Republic, without regard to its conflict of law provisions. The courts of the Czech Republic shall have non-exclusive jurisdiction to settle any dispute or claim arising out of or in connection with this license.

## 9. Support and Updates

Licensor may provide Licensee from time to time with package updates and fixes, according to the Licensor's sole discretion.

The Software is provided without any guaranteed support, updates, or maintenance. CLI Tools may receive updates independently of the main RNGS package.

## 10. Assignment

This license and the rights granted hereunder may not be transferred or assigned by the Licensee without the Licensor's prior written consent. However, if the Licensee undergoes a change of control, merger, or acquisition, the acquiring entity must obtain a new license or negotiate terms with the Licensor.

The Licensor may, at their sole discretion, waive the licensing fee for RNGS and grant a license with a number of Seats that may differ from the standard plans. Such license is governed by the Team license rules. This may be the case especially for individuals maintaining major Open Source Software packages. Licenses granted free of charge are still subject to the other terms set out in this license. Although unlikely, the Licensor at their sole discretion may require free licenses to convert to paid licenses at the then-current rates, with 21 days notice sent via email. The Licensee must then either pay the license fee or remove RNGS from their Products, cease all use and, if requested by the Licensor, certify in writing that they have done so.

## 11. CLI Tools

CLI Tools included in the Software (such as `@react-native-google-signin/config-doctor`) require an active subscription for operation. CLI Tools may:

- Validate subscription status via remote authentication services
- Process local development files (APKs) necessary for the CLI tool functionality
- Collect usage analytics for service improvement in accordance with the Licensor's Privacy Policy.

## 12. Package Distribution and Registry Access

The Software is distributed via a package registry operated by GitHub, a third-party service provider. While GitHub is a well-established and trustworthy platform, access to this registry is provided as a convenience during active subscription but comes with no guarantees, warranties, or service level agreements from the Licensor. The Licensor:

- Makes no guarantees about registry availability, uptime, or continuity
- May change distribution methods or package registries at any time without notice or compensation
- Is not liable for any registry downtime, migration, or discontinuation

The Licensor's only obligation is to provide access to RNGS during the subscription period through whatever distribution method the Licensor deems appropriate at that time. Licensees are solely responsible for configuring registry access on their own systems and infrastructure.

## 13. Survival

Sections 4 (Restrictions), 5 (Termination), 6 (No Warranty), 7 (Limitation of Liability), 8 (Governing Law, Jurisdiction), and this Section 13 shall survive any termination or expiration of this License.
